import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useThemes } from '../../context/ThemesProvider';
import { useAuthUser } from '../../context/UserManager';
import { db } from '../../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

// Styled container for the switch
const SwitchContainer = styled(Box)(({ theme, isdark }: { theme?: any; isdark: number }) => ({
    display: 'flex',
    alignItems: 'center',
    background: isdark ? 'black' : 'white',
    borderRadius: '6px',
    padding: '4px',
    cursor: 'pointer',
    width: 64,
    justifyContent: 'space-between',
    transition: 'background 0.3s',
    position: 'relative',
    height: 32,
}));

// Icon wrapper to help with stacking
const IconWrapper = styled(Box)({
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
});

// Styled thumb (no icon)
const Thumb = styled(Box)(({ theme, isdark }: { theme?: any; isdark: number }) => ({
    width: 28,
    height: 28,
    borderRadius: '4px',
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    position: 'absolute',
    top: 2,
    left: isdark ? 34 : 2,
    transition: 'left 0.3s',
    zIndex: 2,
    pointerEvents: 'none', // allow click through to container
}));

const ThemeToggle: React.FC = () => {
    const { themeIndex, setThemeIndex } = useThemes();
    const { authUser } = useAuthUser();
    const isDark = themeIndex === 1;

    useEffect(() => {
        const applyUserPreference = async () => {
            if (!authUser) return;
            try {
                const snap = await getDoc(doc(db, 'users', authUser.uid));
                if (snap.exists()) {
                    const data = snap.data() as { themePreference?: string };
                    if (data?.themePreference === 'dark') {
                        setThemeIndex(1);
                    } else if (data?.themePreference === 'light') {
                        setThemeIndex(0);
                    }
                }
            } catch (err) {
                console.error('[ThemeToggle] Failed to fetch theme preference', err);
            }
        };
        applyUserPreference();
    }, [authUser, setThemeIndex]);

    const toggleTheme = async () => {
        const nextIndex = isDark ? 0 : 1;
        setThemeIndex(nextIndex);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem('cl-theme-index', String(nextIndex));
            window.localStorage.setItem(
                'cl-theme-mode',
                nextIndex === 1 ? 'dark' : 'light'
            );
        }
        if (authUser) {
            try {
                await updateDoc(doc(db, 'users', authUser.uid), {
                    themePreference: nextIndex === 1 ? 'dark' : 'light',
                });
            } catch (err) {
                console.error('[ThemeToggle] Failed to persist theme preference', err);
            }
        }
    };

    return (
        <SwitchContainer
            isdark={isDark ? 1 : 0}
            onClick={toggleTheme}
            role="button"
            aria-label="Toggle light/dark mode"
        >
            <IconWrapper>
                <DarkModeIcon fontSize="small" />
            </IconWrapper>
            <IconWrapper>
                <LightModeIcon fontSize="small" />
            </IconWrapper>
            <Thumb isdark={isDark ? 1 : 0} />
        </SwitchContainer>
    );
};

export default ThemeToggle;
